import type { CompRow, IssueKind, IssueRecord, LotDetail, LotView, Outcome, SourceHealth, Stance } from "@hammer/core";
import type { AllInBreakdown } from "@hammer/core/money";

export type { LotView, LotDetail, IssueKind, Stance, Outcome, SourceHealth, CompRow };

export interface LotPage extends LotDetail {
  cost: AllInBreakdown | null;
  costBasis: "max" | "current";
  reach: "http" | "client" | "capture";
  sourceLabel: string;
}

export type Issue = IssueRecord & { lotTitle: string | null };

export interface ClientRequest {
  url: string;
  method: "GET" | "POST";
  headers: Record<string, string>;
  body?: string;
}

export type CaptureResponse =
  | { status: "saved"; lotId: string; created: boolean; issues: { kind: IssueKind; detail: string }[] }
  | { status: "preview"; lot: { title: string; url: string; locationText?: string | null; images?: string[] }; obs: { currentBidCents: number | null; bidCount: number | null; endAt: string | null; sourceState: string }; issues: { kind: IssueKind; detail: string }[]; existingId: string | null }
  | { status: "needs_client"; request: ClientRequest }
  | { status: "error"; message: string };

export interface Settings {
  homeLabel: string | null;
  home: { lat: number; lng: number } | null;
  taxBps: number;
  defaultPremiumBps: number;
  transportCentsPerMile: number;
  premiumBpsBySource: Record<string, number>;
  feesCentsBySource: Record<string, number>;
  quietHours: { start: number; end: number } | null;
  notify: { closingSoon: boolean; overMax: boolean; stillCheap: boolean; fetchFailing: boolean };
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public data: Record<string, unknown> | null,
  ) {
    super((data?.message as string) ?? (data?.error as string) ?? (data?.warning as string) ?? `Request failed (${status})`);
  }
}

export async function api<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method: init.method ?? "GET",
    headers: init.body !== undefined ? { "content-type": "application/json" } : undefined,
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    credentials: "same-origin",
  });
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { error: text };
  }
  if (res.status === 401 && path !== "/login") window.dispatchEvent(new Event("hammer:logged-out"));
  if (!res.ok) throw new ApiError(res.status, data as Record<string, unknown>);
  return data as T;
}

/**
 * Make a request the server can't (sites that refuse servers but answer browsers).
 * Returns status 0 when the browser couldn't reach the site at all (offline, or the site disallows it).
 */
export async function clientFetch(req: ClientRequest): Promise<{ status: number; body: string; durationMs: number }> {
  const t0 = performance.now();
  try {
    const r = await fetch(req.url, { method: req.method, headers: req.headers, body: req.body, mode: "cors", credentials: "omit" });
    return { status: r.status, body: await r.text(), durationMs: Math.round(performance.now() - t0) };
  } catch (e) {
    return { status: 0, body: String(e), durationMs: Math.round(performance.now() - t0) };
  }
}
